import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  ANCHOR_TECHNIQUES,
  MOORING_TECHNIQUES,
  type AnchorTechnique,
  type MooringTechnique,
} from "@nav-games/anchoring";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface TechniquesProps {
  onNavigate: (to: string) => void;
}

const MODULE = "anchoring";
const GAME = "berthing-techniques";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

type Domain = "mooring" | "anchor";

interface Card {
  domain: Domain;
  prompt: string;
  answerId: string;
  choices: ReadonlyArray<{ id: string; label: string }>;
  reveal: string;
}

const MOORING_SCENARIOS: ReadonlyArray<{ id: string; prompt: string }> = [
  {
    id: "alongside-springs",
    prompt:
      "Tidal harbour, finger pontoon, overnight stay forecast F4 gusts F5. Wind-against-tide expected. Which moor?",
  },
  {
    id: "alongside-springs",
    prompt:
      "Solent marina overnight in normal conditions. Need to prevent the boat from surging fore-and-aft. Which moor?",
  },
  {
    id: "alongside",
    prompt:
      "Five-minute fuel stop on a calm windless day, no current, no surge. Simplest acceptable moor?",
  },
  {
    id: "lazy-lines",
    prompt:
      "Italian marina, finger berth + pre-laid lazy-line ground tackle for your bow. Stern-to the quay. Which technique?",
  },
  {
    id: "med-stern-to",
    prompt:
      "Greek harbour with no marina staff or lazy lines — you drop your own anchor and back in to the quay. Which technique?",
  },
  {
    id: "raft-up",
    prompt:
      "All pontoon space is taken; harbour master directs you to tie alongside an already-moored yacht. Where do your bow and stern lines go?",
  },
  {
    id: "trot-mooring",
    prompt:
      "Beaulieu River, allocated a numbered mooring buoy on a rolled trot. Bow strop to which?",
  },
  {
    id: "fore-and-aft",
    prompt:
      "River anchorage, strong cross-tide, no room to swing on a single mooring. Which technique?",
  },
];

const ANCHOR_SCENARIOS: ReadonlyArray<{ id: string; prompt: string }> = [
  {
    id: "single-bower",
    prompt:
      "Sheltered bay, lots of swinging room, overnight with steady wind forecast. Which anchoring technique?",
  },
  {
    id: "bahamian-moor",
    prompt:
      "Crowded tidal anchorage where the tide reverses every six hours and you don't want to swing on a 360° circle. Which technique?",
  },
  {
    id: "fore-and-aft-anchor",
    prompt:
      "Sheltered river spot with cross-tide; you need the boat held in a fixed orientation parallel to the shore. Which technique?",
  },
  {
    id: "tandem",
    prompt:
      "Hurricane forecast, you're at anchor in a hurricane hole and need maximum holding from one direction. Which technique?",
  },
  {
    id: "kedge-from-stern",
    prompt:
      "Soft mud grounding on a falling tide. Sea anchor isn't relevant — you want to get the boat OFF. Which technique?",
  },
  {
    id: "anchor-watch",
    prompt:
      "Overnight at anchor, wind shift + pressure drop forecast. What's the most important crew duty?",
  },
];

function shuffled<T>(arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

function buildCard(): Card {
  const domain: Domain = Math.random() < 0.5 ? "mooring" : "anchor";
  if (domain === "mooring") {
    const sc =
      MOORING_SCENARIOS[Math.floor(Math.random() * MOORING_SCENARIOS.length)]!;
    const answer = MOORING_TECHNIQUES.find((t) => t.id === sc.id)!;
    const choices = shuffled(
      MOORING_TECHNIQUES.map((t) => ({ id: t.id, label: t.name })),
    ).slice(0, CHOICES_PER_CARD);
    if (!choices.find((c) => c.id === answer.id))
      choices[0] = { id: answer.id, label: answer.name };
    return {
      domain,
      prompt: sc.prompt,
      answerId: answer.id,
      choices: shuffled(choices),
      reveal: `${answer.summary} (${answer.whenToUse})`,
    };
  }
  const sc =
    ANCHOR_SCENARIOS[Math.floor(Math.random() * ANCHOR_SCENARIOS.length)]!;
  const answer = ANCHOR_TECHNIQUES.find((t) => t.id === sc.id)!;
  const choices = shuffled(
    ANCHOR_TECHNIQUES.map((t) => ({ id: t.id, label: t.name })),
  ).slice(0, CHOICES_PER_CARD);
  if (!choices.find((c) => c.id === answer.id))
    choices[0] = { id: answer.id, label: answer.name };
  return {
    domain,
    prompt: sc.prompt,
    answerId: answer.id,
    choices: shuffled(choices),
    reveal: `${answer.summary} (${answer.whenToUse})`,
  };
}

export function Techniques({ onNavigate }: TechniquesProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (id: string) => {
      if (chosen || result) return;
      const ok = id === card.answerId;
      const attempt: Omit<Attempt, "module"> = {
        char: `${card.domain}-${card.answerId}`,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(id);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/anchoring")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "anchoring" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION} ·{" "}
        {card.domain === "mooring" ? "mooring techniques" : "anchoring extras"}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          {card.domain === "mooring"
            ? "Which mooring technique?"
            : "Which anchoring technique?"}
        </h3>
        <p style={{ marginBottom: 12 }}>{card.prompt}</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c.id === card.answerId;
            const isChosen = c.id === chosen;
            return (
              <button
                key={c.id}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c.id)}
                style={{
                  fontSize: 14,
                  padding: "12px 6px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.answerId
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.answerId ? "✓ Correct." : "✗ Not quite."}
            </p>
            <p className="small" style={{ margin: "8px 0 0" }}>
              {card.reveal}
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/anchoring")}>
        ← Back to Anchoring
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Anchoring
        </button>
      </div>
    </>
  );
}
